import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { CategoryDefDto, ContactCreate, ContactDetail, ContactPickItem, RatingGrade } from '@tww/shared';
import { SALUTATIONS } from '@tww/shared';
import { api, ApiRequestError, qs } from '../../api/client';
import { useCategoryDefs, useLookups } from '../../api/hooks';
import { ComboField, Select, TextArea, TextInput } from '../../ui/fields';
import { ContactPicker } from '../../ui/ContactPicker';
import { ErrorState, Note, RatingBadge, Skeleton } from '../../ui/basics';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/Toast';
import { AttributeFields } from './attributes';

interface FormState {
  kind: 'person' | 'organisation';
  salutation: string;
  title: string;
  first_name: string;
  last_name: string;
  org_name: string;
  position: string;
  organisation: ContactPickItem | null;
  street: string;
  zip: string;
  city: string;
  state_code: string;
  region: string;
  phone: string;
  mobile: string;
  email: string;
  website: string;
  notes: string;
  categories: string[];
  attributes: Record<string, Record<string, unknown>>;
  rating_grade: RatingGrade | '';
  rating_reason: string;
}

function fromDetail(c: ContactDetail | null, preset: Partial<FormState> = {}): FormState {
  return {
    kind: c?.kind ?? 'person',
    salutation: c?.salutation ?? '',
    title: c?.title ?? '',
    first_name: c?.first_name ?? '',
    last_name: c?.last_name ?? '',
    org_name: c?.org_name ?? '',
    position: c?.position ?? '',
    organisation: c?.organisation ? { id: c.organisation.id, display_name: c.organisation.display_name, sub: c.organisation.city, rating_grade: null, rating_reason: null, city: c.organisation.city, region: c.organisation.region, last_price: null, categories: [] } : null,
    street: c?.street ?? '',
    zip: c?.zip ?? '',
    city: c?.city ?? '',
    state_code: c?.state_code ?? '',
    region: c?.region ?? '',
    phone: c?.phone ?? '',
    mobile: c?.mobile ?? '',
    email: c?.email ?? '',
    website: c?.website ?? '',
    notes: c?.notes ?? '',
    categories: c?.categories ?? [],
    attributes: c?.attributes ?? {},
    rating_grade: '',
    rating_reason: '',
    ...preset,
  };
}
const nn = (s: string) => (s.trim() === '' ? null : s.trim());

/** Feldnamen für die Fehlerliste */
const FIELD_LABELS: Record<string, string> = {
  kind: 'Art',
  salutation: 'Anrede',
  title: 'Titel',
  first_name: 'Vorname',
  last_name: 'Nachname',
  org_name: 'Name',
  position: 'Position',
  organisation_id: 'Organisation',
  street: 'Straße und Hausnummer',
  zip: 'PLZ',
  city: 'Ort',
  state_code: 'Bundesland',
  region: 'Region',
  phone: 'Telefon',
  mobile: 'Mobil',
  email: 'E-Mail',
  website: 'Website',
  notes: 'Notizen',
  categories: 'Kategorien',
  rating: 'Bewertung',
};

/** Kontakt anlegen (/kontakte/neu) und bearbeiten (/kontakte/:id/bearbeiten) als eigene Seite: Abschnitte links, Zusammenfassung und Aktionen rechts. */
export function ContactFormPage() {
  const { id } = useParams();
  const cid = id ? Number(id) : null;
  const [sp] = useSearchParams();
  const existing = useQuery({ queryKey: ['contact', cid], queryFn: () => api.get<ContactDetail>(`/contacts/${cid}`), enabled: cid != null });
  if (cid != null && existing.isLoading) return <Skeleton rows={8} />;
  if (cid != null && (existing.isError || !existing.data)) return <ErrorState error={existing.error ?? new Error('Kontakt nicht gefunden')} />;
  const preset: Partial<FormState> = {};
  const cat = sp.get('kategorie');
  if (cat) preset.categories = [cat];
  if (sp.get('art') === 'organisation') preset.kind = 'organisation';
  return <ContactFormInner key={cid ?? 'new'} contact={existing.data ?? null} preset={preset} />;
}

function ContactFormInner({ contact, preset }: { contact: ContactDetail | null; preset: Partial<FormState> }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const defs = useCategoryDefs();
  const lookups = useLookups();
  const [f, setF] = useState<FormState>(() => fromDetail(contact, preset));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setF((s) => ({ ...s, [k]: v }));
    // Fehler am Feld verschwindet mit der Korrektur
    setErrors((e) => {
      if (!(k in e)) return e;
      const { [k]: _gone, ...rest } = e;
      return rest;
    });
  };

  const states = lookups.data?.find((l) => l.key === 'states')?.values ?? [];
  const regions = (lookups.data?.find((l) => l.key === 'regions')?.values ?? []).filter((r) => r.is_active && (!f.state_code || !r.meta?.state || r.meta.state === f.state_code));
  const activeDefs = (defs.data ?? []).filter((d) => d.is_active);
  const allowed = (d: CategoryDefDto) => d.kinds_allowed.includes(f.kind);
  const displayName = f.kind === 'person' ? [f.title, f.first_name, f.last_name].filter((s) => s.trim()).join(' ') : f.org_name.trim();

  const [dupKey, setDupKey] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDupKey(`${displayName}|${f.email}`), 400);
    return () => clearTimeout(t);
  }, [displayName, f.email]);
  const similar = useQuery({
    queryKey: ['similar', dupKey],
    queryFn: () => {
      const [name, email] = dupKey.split('|');
      return api.get<{ items: { id: number; display_name: string; org_name: string | null; city: string | null }[] }>(`/contacts/similar${qs({ name, email, exclude: contact?.id })}`).then((r) => r.items);
    },
    enabled: dupKey.replace('|', '').length >= 3,
  });

  const save = useMutation({
    mutationFn: async () => {
      const body: ContactCreate = {
        kind: f.kind,
        salutation: (f.salutation || null) as ContactCreate['salutation'],
        title: nn(f.title),
        first_name: nn(f.first_name),
        last_name: nn(f.last_name),
        org_name: nn(f.org_name),
        position: nn(f.position),
        organisation_id: f.organisation?.id ?? null,
        street: nn(f.street),
        zip: nn(f.zip),
        city: nn(f.city),
        state_code: nn(f.state_code),
        region: nn(f.region),
        phone: nn(f.phone),
        mobile: nn(f.mobile),
        email: nn(f.email) ?? '',
        website: nn(f.website),
        notes: nn(f.notes),
        categories: f.categories.map((k, i) => ({ category_key: k, attributes: f.attributes[k] ?? {}, primary: i === 0 })),
        rating: !contact && f.rating_grade ? { grade: f.rating_grade, reason: f.rating_reason } : null,
      };
      if (contact) {
        const { rating: _r, ...patch } = body;
        return api.patch<ContactDetail>(`/contacts/${contact.id}`, { ...patch, updated_at: contact.updated_at });
      }
      return api.post<ContactDetail>('/contacts', body);
    },
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['contact', c.id] });
      qc.invalidateQueries({ queryKey: ['lookups'] });
      toast(contact ? 'Kontakt gespeichert' : 'Kontakt angelegt');
      nav(`/kontakte/${c.id}`);
    },
    onError: (e) => {
      if (e instanceof ApiRequestError) {
        setErrors(e.fields ?? {});
        toast(e.message, 'error');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else toast('Speichern fehlgeschlagen', 'error');
    },
  });
  const err = (k: string) => errors[k];
  const attrErrors = useMemo(() => {
    const out: Record<string, Record<string, string>> = {};
    for (const [k, v] of Object.entries(errors)) {
      const m = /^attributes\.([^.]+)\.(.+)$/.exec(k);
      if (m) (out[m[1]!] ??= {})[m[2]!] = v;
    }
    return out;
  }, [errors]);
  const errorLabel = (k: string) => {
    const m = /^attributes\.([^.]+)\.([^.]+)/.exec(k);
    if (m) {
      const d = activeDefs.find((x) => x.key === m[1]);
      const field = d?.fields.find((x) => x.key === m[2])?.label;
      return [d ? (d.key === 'forester' ? 'Forstprofil' : d.name) : null, field].filter(Boolean).join(', ') || null;
    }
    return FIELD_LABELS[k.split('.')[0]!] ?? null;
  };
  const back = contact ? `/kontakte/${contact.id}` : '/kontakte';
  const canSave = f.categories.length > 0 && (f.kind === 'person' ? f.last_name.trim() : f.org_name.trim());

  return (
    <>
      <div className="crumb">
        <Link to="/kontakte">Kontakte</Link>
        {contact && (
          <>
            {' / '}
            <Link to={back}>{contact.display_name}</Link>
          </>
        )}
      </div>
      <div className="head">
        <div>
          <h1>{contact ? 'Kontakt bearbeiten' : 'Neuer Kontakt'}</h1>
          {contact && <div className="sub">{contact.display_name}</div>}
        </div>
      </div>
      {Object.keys(errors).length > 0 && (
        <div className="form-errors" role="alert">
          Bitte prüfen:
          <ul>
            {Object.entries(errors).map(([k, v]) => {
              const l = errorLabel(k);
              return <li key={k}>{l ? `${l}: ${v}` : v}</li>;
            })}
          </ul>
        </div>
      )}
      <form
        className="page-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSave) save.mutate();
        }}
      >
        <div className="col">
          <section className="section">
            <div className="sh">
              <h2>Art und Kategorien</h2>
            </div>
            <div className="sb" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="kind-switch" role="radiogroup" aria-label="Art">
                <button type="button" role="radio" aria-checked={f.kind === 'person'} className={f.kind === 'person' ? 'on' : ''} onClick={() => set('kind', 'person')}>
                  <Icon name="user" /> Person
                </button>
                <button type="button" role="radio" aria-checked={f.kind === 'organisation'} className={f.kind === 'organisation' ? 'on' : ''} onClick={() => set('kind', 'organisation')}>
                  <Icon name="building" /> Organisation
                </button>
              </div>
              <div className="cat-grid">
                {activeDefs.map((d) => {
                  const on = f.categories.includes(d.key);
                  const ok = allowed(d);
                  return (
                    <label key={d.key} className={`cat-card ${on ? 'on' : ''} ${ok ? '' : 'off'}`} title={ok ? '' : `Nicht für ${f.kind === 'person' ? 'Personen' : 'Organisationen'}`}>
                      <input type="checkbox" checked={on} disabled={!ok} onChange={(e) => set('categories', e.target.checked ? [...f.categories, d.key] : f.categories.filter((k) => k !== d.key))} />
                      {d.name}
                    </label>
                  );
                })}
              </div>
              {err('categories') && <span className="err miss small">{err('categories')}</span>}
            </div>
          </section>

          <section className="section">
            <div className="sh">
              <h2>{f.kind === 'person' ? 'Person' : 'Organisation'}</h2>
            </div>
            <div className="sb">
              {f.kind === 'person' ? (
                <div className="form">
                  <div className="form full contact-name-row">
                    <Select label="Anrede" value={f.salutation} onChange={(v) => set('salutation', v)} options={SALUTATIONS.map((s) => ({ value: s, label: s }))} />
                    <TextInput label="Titel" value={f.title} onChange={(v) => set('title', v)} placeholder="Dr." error={err('title')} />
                    <TextInput label="Vorname" value={f.first_name} onChange={(v) => set('first_name', v)} error={err('first_name')} />
                  </div>
                  <TextInput label="Nachname" value={f.last_name} onChange={(v) => set('last_name', v)} required error={err('last_name')} autoFocus={!contact} />
                  <TextInput label="Position" value={f.position} onChange={(v) => set('position', v)} placeholder="Revierleitung, Ansprechpartner" error={err('position')} />
                  <div className="full">
                    <ContactPicker label="Gehört zu Organisation" value={f.organisation} onChange={(v) => set('organisation', v)} allowCreate={false} placeholder="Forstamt, Firma, Gemeinde …" hint="Adresse und Region der Organisation gelten, wenn eigene Felder leer sind" />
                  </div>
                </div>
              ) : (
                <div className="form one">
                  <TextInput label="Name" value={f.org_name} onChange={(v) => set('org_name', v)} required error={err('org_name')} autoFocus={!contact} placeholder="Forstamt Heide, Taunushof, Gemeinde Wehrheim" />
                </div>
              )}
              {similar.data && similar.data.length > 0 && (
                <Note kind="warn">
                  Ähnlicher Kontakt vorhanden:{' '}
                  {similar.data.map((s, i) => (
                    <span key={s.id}>
                      {i > 0 && ', '}
                      <Link className="link" to={`/kontakte/${s.id}`} target="_blank">
                        {s.display_name}
                        {s.org_name ? `, ${s.org_name}` : s.city ? `, ${s.city}` : ''}
                      </Link>
                    </span>
                  ))}
                </Note>
              )}
            </div>
          </section>

          <section className="section">
            <div className="sh">
              <h2>Kontakt und Adresse</h2>
            </div>
            <div className="sb">
              <div className="form">
                <TextInput label="Telefon" value={f.phone} onChange={(v) => set('phone', v)} type="tel" error={err('phone')} />
                <TextInput label="Mobil" value={f.mobile} onChange={(v) => set('mobile', v)} type="tel" error={err('mobile')} />
                <TextInput label="E-Mail" value={f.email} onChange={(v) => set('email', v)} type="email" error={err('email')} />
                <TextInput label="Website" value={f.website} onChange={(v) => set('website', v)} error={err('website')} />
                <TextInput label="Straße und Hausnummer" value={f.street} onChange={(v) => set('street', v)} error={err('street')} />
                <div className="form zip-city">
                  <TextInput label="PLZ" value={f.zip} onChange={(v) => set('zip', v)} error={err('zip')} />
                  <TextInput label="Ort" value={f.city} onChange={(v) => set('city', v)} error={err('city')} />
                </div>
                <Select label="Bundesland" value={f.state_code} onChange={(v) => set('state_code', v)} options={states.map((s) => ({ value: s.code, label: s.label }))} error={err('state_code')} />
                <ComboField label="Region" value={f.region} onChange={(v) => set('region', v)} error={err('region')} options={regions.map((r) => ({ value: r.code, label: r.label }))} newHint="wird als neue Region gespeichert" />
              </div>
            </div>
          </section>

          {f.categories.map((k) => {
            const d = activeDefs.find((x) => x.key === k);
            if (!d || !d.fields.length) return null;
            return (
              <section key={k} className="section">
                <div className="sh">
                  <h2>{d.key === 'forester' ? 'Forstprofil' : d.name}</h2>
                </div>
                <div className="sb">
                  <div className="form">
                    <AttributeFields def={d} value={f.attributes[k] ?? {}} onChange={(v) => set('attributes', { ...f.attributes, [k]: v })} errors={attrErrors[k]} lookups={lookups.data} />
                  </div>
                </div>
              </section>
            );
          })}

          {!contact && (
            <section className="section">
              <div className="sh">
                <h2>Erste Bewertung</h2>
                <span className="desc">optional, später in der Akte änderbar</span>
              </div>
              <div className="sb">
                <div className="form">
                  <div className="field">
                    <label>Bewertung</label>
                    <div className="seg" role="radiogroup">
                      {(['', 'A', 'B', 'C'] as const).map((g) => (
                        <button key={g || 'none'} type="button" role="radio" aria-checked={f.rating_grade === g} className={f.rating_grade === g ? 'on' : ''} onClick={() => set('rating_grade', g)}>
                          {g ? <RatingBadge grade={g} /> : 'keine'} {g === 'A' ? 'super' : g === 'B' ? 'ok' : g === 'C' ? 'nicht wieder' : ''}
                        </button>
                      ))}
                    </div>
                  </div>
                  <TextInput label="Begründung" value={f.rating_reason} onChange={(v) => set('rating_reason', v)} />
                </div>
              </div>
            </section>
          )}

          <section className="section">
            <div className="sh">
              <h2>Notizen</h2>
            </div>
            <div className="sb">
              <div className="form one">
                <TextArea label="" value={f.notes} onChange={(v) => set('notes', v)} rows={4} placeholder="Freie Notizen, die nicht in ein Feld passen" />
              </div>
            </div>
          </section>
        </div>

        <aside className="aside">
          <div className="section">
            <div className="sh">
              <h2>Zusammenfassung</h2>
            </div>
            <div className="sb summary-box">
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {!contact && <RatingBadge grade={f.rating_grade || null} />}
                <strong style={{ fontSize: 16 }}>{displayName || <span className="mut">Noch kein Name</span>}</strong>
              </div>
              <dl>
                <dt>Art</dt>
                <dd>{f.kind === 'person' ? 'Person' : 'Organisation'}</dd>
                <dt>Kategorien</dt>
                <dd>{f.categories.length ? f.categories.map((k) => activeDefs.find((d) => d.key === k)?.name ?? k).join(', ') : <span className="miss">mindestens eine wählen</span>}</dd>
                {(f.state_code || f.region) && (
                  <>
                    <dt>Region</dt>
                    <dd>{[states.find((s) => s.code === f.state_code)?.label, f.region].filter(Boolean).join(', ')}</dd>
                  </>
                )}
                {(f.email || f.mobile || f.phone) && (
                  <>
                    <dt>Kontakt</dt>
                    <dd>{[f.mobile || f.phone, f.email].filter(Boolean).join(' · ')}</dd>
                  </>
                )}
              </dl>
            </div>
          </div>
          <div className="actions-col">
            <button className="btn primary" type="submit" disabled={!canSave || save.isPending}>
              {save.isPending ? 'Wird gespeichert …' : contact ? 'Änderungen speichern' : 'Kontakt anlegen'}
            </button>
            <Link className="btn" to={back}>
              Abbrechen
            </Link>
          </div>
        </aside>
      </form>
    </>
  );
}
